import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

const SubscriptionContext = createContext();

export function SubscriptionProvider({ children }) {
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchSubscription();

        // Subscribe to auth changes
        const { data: authListener } = supabase.auth.onAuthStateChange(() => {
            fetchSubscription();
        });

        return () => {
            authListener?.subscription?.unsubscribe();
        };
    }, []);

    const fetchSubscription = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();

            if (!user) {
                setSubscription({ plan_type: 'free', status: 'active' });
                setLoading(false);
                return;
            }

            const { data, error } = await supabase
                .from('subscriptions')
                .select('*')
                .eq('user_id', user.id)
                .in('status', ['active', 'trialing'])
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            if (error) {
                console.error('Error fetching subscription:', error);
            }

            setSubscription(data || { plan_type: 'free', status: 'active' });
            setLoading(false);
        } catch (error) {
            console.error('Error in fetchSubscription:', error);
            setSubscription({ plan_type: 'free', status: 'active' });
            setLoading(false);
        }
    };

    const getPlanLimits = () => {
        const plan = subscription?.plan_type || 'free';
        switch (plan) {
            case 'growth':
                return {
                    projects: Infinity,
                    screensPerProject: Infinity,
                    publishedFlows: Infinity,
                    monthlyViews: 250000,
                    variants: Infinity,
                    analyticsRetentionDays: Infinity,
                    abTesting: true,
                    aiGenerations: 100,
                    aiModifications: 500
                };
            case 'starter':
                return {
                    projects: 3,
                    screensPerProject: 10,
                    publishedFlows: Infinity,
                    monthlyViews: 100000,
                    variants: 3,
                    analyticsRetentionDays: Infinity,
                    abTesting: true,
                    aiGenerations: 25,
                    aiModifications: 100
                };
            case 'free':
            default:
                return {
                    projects: 1,
                    screensPerProject: 3,
                    publishedFlows: 1,
                    monthlyViews: 10000,
                    variants: 0,
                    analyticsRetentionDays: 7,
                    abTesting: false,
                    aiGenerations: 3,
                    aiModifications: 10
                };
        }
    };

    const canCreateProject = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return false;

        const { count } = await supabase
            .from('projects')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id);

        const limits = getPlanLimits();
        return count < limits.projects;
    };

    const canAddScreen = (currentScreenCount) => {
        // Allow building unlimited screens, check limits only on publish
        return true;
    };

    const checkProjectLimits = (project) => {
        const limits = getPlanLimits();
        const screenCount = project.screens?.length || 0;

        if (limits.screensPerProject !== Infinity && screenCount > limits.screensPerProject) {
            return {
                allowed: false,
                reason: 'screens',
                limit: limits.screensPerProject,
                current: screenCount
            };
        }
        return { allowed: true };
    };

    const canPublish = async (projectId) => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return false;

        // Count published flows for this user
        const { count } = await supabase
            .from('projects')
            .select('published_count', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .gt('published_count', 0); // Only count projects that have been published

        const limits = getPlanLimits();
        return count < limits.publishedFlows;
    };

    const canUseABTesting = () => {
        const limits = getPlanLimits();
        return limits.abTesting;
    };

    const canCreateVariant = (currentVariantCount) => {
        const limits = getPlanLimits();
        return currentVariantCount < limits.variants;
    };

    const getCurrentMonthViews = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return 0;

        const startOfMonth = new Date();
        startOfMonth.setDate(1);
        startOfMonth.setHours(0, 0, 0, 0);

        // Get all user's projects
        const { data: projects } = await supabase
            .from('projects')
            .select('id')
            .eq('user_id', user.id);

        if (!projects || projects.length === 0) return 0;

        const projectIds = projects.map(p => p.id);

        // Count unique sessions (users) from analytics_events
        // We use a raw query to count distinct session_ids
        const { data, error } = await supabase
            .rpc('count_unique_users', {
                project_ids: projectIds,
                start_date: startOfMonth.toISOString()
            });

        if (error) {
            console.error('Error counting unique users:', error);
            // Fallback to old method if RPC fails
            const { count } = await supabase
                .from('flow_views')
                .select('*', { count: 'exact', head: true })
                .eq('user_id', user.id)
                .gte('viewed_at', startOfMonth.toISOString());
            return count || 0;
        }

        return data || 0;
    };

    const hasReachedViewLimit = async () => {
        const currentViews = await getCurrentMonthViews();
        const limits = getPlanLimits();
        return currentViews >= limits.monthlyViews;
    };

    const canUseAIGeneration = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return { allowed: false, reason: 'Not authenticated' };

        // Check monthly limit
        const { data: monthlyCount, error: monthlyError } = await supabase
            .rpc('count_monthly_ai_usage', {
                p_user_id: user.id,
                p_usage_type: 'generation'
            });

        if (monthlyError) {
            console.error('Error checking monthly AI usage:', monthlyError);
            return { allowed: false, reason: 'Error checking usage' };
        }

        const limits = getPlanLimits();
        if (monthlyCount >= limits.aiGenerations) {
            return {
                allowed: false,
                reason: 'monthly_limit',
                current: monthlyCount,
                limit: limits.aiGenerations
            };
        }

        // Check hourly rate limit (10 per hour)
        const { data: hourlyCount, error: hourlyError } = await supabase
            .rpc('count_hourly_ai_usage', {
                p_user_id: user.id,
                p_usage_type: 'generation'
            });

        if (hourlyError) {
            console.error('Error checking hourly AI usage:', hourlyError);
        } else if (hourlyCount >= 10) {
            return {
                allowed: false,
                reason: 'rate_limit',
                message: 'Too many requests. Please wait an hour.'
            };
        }

        return { allowed: true, current: monthlyCount, limit: limits.aiGenerations };
    };

    const trackAIUsage = async (usageType, projectId = null) => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        await supabase.from('ai_usage').insert({
            user_id: user.id,
            usage_type: usageType,
            project_id: projectId
        });
    };

    const getMonthlyAIUsage = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return { generations: 0, modifications: 0 };

        const { data: genCount } = await supabase
            .rpc('count_monthly_ai_usage', {
                p_user_id: user.id,
                p_usage_type: 'generation'
            });

        const { data: modCount } = await supabase
            .rpc('count_monthly_ai_usage', {
                p_user_id: user.id,
                p_usage_type: 'modification'
            });

        return {
            generations: genCount || 0,
            modifications: modCount || 0
        };
    };

    return (
        <SubscriptionContext.Provider
            value={{
                subscription,
                loading,
                canCreateProject,
                canAddScreen,
                canPublish,
                checkProjectLimits,
                getPlanLimits,
                canUseABTesting,
                canCreateVariant,
                getCurrentMonthViews,
                hasReachedViewLimit,
                canUseAIGeneration,
                trackAIUsage,
                getMonthlyAIUsage,
                refresh: fetchSubscription
            }}
        >
            {children}
        </SubscriptionContext.Provider>
    );
}

export const useSubscription = () => {
    const context = useContext(SubscriptionContext);
    if (!context) {
        throw new Error('useSubscription must be used within SubscriptionProvider');
    }
    return context;
};
